import {
  EntityState,
  EntityAdapter,
  createEntityAdapter,
  Update,
} from '@ngrx/entity';
import { createReducer, on, Action } from '@ngrx/store';

import * as CasualTournamentActions from './casual-tournament.actions';
import {
  CasualTournamentEntity,
  RoomFilterModel,
  TournamentType,
} from '@fantaz/shared/data-access/models';
import { loadCasualTournamentDetailsSubscribed } from '../casual-tournament-details/casual-tournament-details.actions';
import { ITimesPlayedInTournament } from '@fantaz/shared/data-access/api';

export const CASUAL_TOURNAMENT_FEATURE_KEY = 'casualTournament';

export interface CasualTournamentsState
  extends EntityState<CasualTournamentEntity> {
  filters?: RoomFilterModel[];
  lastTab?: TournamentType;
  filterByGames?: string[];
  searchTerm?: string;
  selectedId?: string | number; // which CasualTournament record has been selected
  loaded: boolean; // has the CasualTournament list been loaded
  error?: string | null; // last known error (if any)
  timesUserTournamentPlayed: ITimesPlayedInTournament[] | null;
}

export interface CasualTournamentPartialState {
  readonly [CASUAL_TOURNAMENT_FEATURE_KEY]: CasualTournamentsState;
}

export const casualTournamentAdapter: EntityAdapter<CasualTournamentEntity> =
  createEntityAdapter<CasualTournamentEntity>({
    selectId: (tournaments: CasualTournamentEntity) => tournaments.uuid,
  });

export const initialCasualTournamentState: CasualTournamentsState =
  casualTournamentAdapter.getInitialState({
    // set initial required properties
    filters: [],
    loaded: false,
    timesUserTournamentPlayed: null,
  });

const reducer = createReducer(
  initialCasualTournamentState,
  on(
    CasualTournamentActions.loadCasualTournaments,
    (state: CasualTournamentsState) => ({
      ...state,
      loaded: false,
      error: null,
    }),
  ),
  on(
    CasualTournamentActions.loadCasualTournamentSuccess,
    (state, { casualTournament }) => {
      return casualTournamentAdapter.setAll(casualTournament, {
        ...state,
        loaded: true,
      });
    },
  ),
  on(
    CasualTournamentActions.loadCasualTournamentFailure,
    (state, { error }) => ({ ...state, error }),
  ),
  on(CasualTournamentActions.filterTournamentsByGames, (state, { games }) => ({
    ...state,
    filterByGames: games,
  })),
  on(CasualTournamentActions.clearFilterTournamentsByGames, (state) => ({
    ...state,
    filterByGames: [],
  })),
  on(CasualTournamentActions.searchTournaments, (state, { searchTerm }) => ({
    ...state,
    searchTerm,
  })),
  on(
    CasualTournamentActions.loadSubscriptionTournamentSuccess,
    (state, { eventPrototypeUuid, eventSchedule, status }) => {
      const eventScheduleToUpdate = {
        ...eventSchedule,
        subscriptionStatus: status,
      };
      const eventSchedules = [];
      eventSchedules.push(eventScheduleToUpdate);
      const update: Update<CasualTournamentEntity> = {
        id: eventPrototypeUuid,
        changes: { eventSchedules },
      };
      return casualTournamentAdapter.updateOne(update, state);
    },
  ),
  on(
    loadCasualTournamentDetailsSubscribed,
    (state, { eventPrototypeUuid, eventScheduleUuid, status }) => {
      let result = state;
      if (eventPrototypeUuid) {
        const eventPrototype = state.entities[eventPrototypeUuid];
        const eventSchedule = eventPrototype?.eventSchedules.find(
          (e) => e.uuid === eventScheduleUuid,
        );
        if (eventSchedule) {
          const eventScheduleToUpdate = {
            ...eventSchedule,
            subscriptionStatus: status,
          };
          const eventSchedules = [];
          eventSchedules.push(eventScheduleToUpdate);
          const update: Update<CasualTournamentEntity> = {
            id: eventPrototypeUuid as string,
            changes: { eventSchedules },
          };
          result = casualTournamentAdapter.updateOne(update, state);
        }
      }
      return result;
    },
  ),
  on(CasualTournamentActions.clearSearchTournaments, (state) => ({
    ...state,
    searchTerm: '',
  })),
  on(CasualTournamentActions.addRoomFilter, (state, { filters }) => ({
    ...state,
    filters,
  })),
  on(CasualTournamentActions.clearRoomFilters, (state) => ({
    ...state,
    filters: [],
  })),
  on(CasualTournamentActions.addLastTab, (state, { lastTab }) => ({
    ...state,
    lastTab,
  })),
  on(CasualTournamentActions.clearLastTab, (state) => ({
    ...state,
    lastTab: undefined,
  })),
  on(
    CasualTournamentActions.getTimesUserTournamentPlayedSuccess,
    (state, { timesPlayedInTournament }) => ({
      ...state,
      timesUserTournamentPlayed: timesPlayedInTournament,
    }),
  ),
);

export function casualTournamentReducer(
  state: CasualTournamentsState | undefined,
  action: Action,
) {
  return reducer(state, action);
}
